/**
 * useAccountTimezoneState — les gestes de l'écran « Fuseau horaire » : l'interrupteur, le choix
 * d'un fuseau, la sortie.
 *
 * Une seule écriture, `PUT api/user/timezone`, par `useApiMutation` :
 *  - rallumer l'automatique → `{ auto: true }` (le serveur prend aussitôt le fuseau de l'appareil) ;
 *  - couper l'automatique → `{ auto: false, timezone: <le fuseau en cours> }` : couper sans choisir
 *    FIGE le fuseau du moment, comme Slack ou Google Agenda. Un compte sans fuseau n'a rien à figer :
 *    la liste s'ouvre, et rien ne part tant qu'on n'a pas choisi ;
 *  - choisir un fuseau → `{ auto: false, timezone }`.
 *
 * La réponse est le `UserResource` : le réducteur `currentUser` la range, et c'est elle qui fait
 * foi. En attendant, l'interrupteur montre le geste demandé (`pendingAuto`) ; un refus le remet
 * où il était et dit pourquoi. La liste, elle, est demandée dès le geste (`onManual`), sans
 * attendre la réponse de l'écriture.
 */
import { useCallback, useReducer } from "react"
import { useNavigation } from "@react-navigation/native"

import { authService } from "../../../../../../data/services/authService"
import { useApiMutation } from "../../../hooks/useApiMutation"

export const TIMEZONE_SAVE_FAILED = "Le fuseau n'a pas pu être enregistré. Nouvel essai possible dans un instant."

export type TimezoneUiState = {
    /** L'interrupteur demandé, tant que le serveur n'a pas répondu. */
    pendingAuto: boolean | null
    /** Le fuseau choisi en cours d'envoi — sa ligne attend. */
    pendingTimezone: string | null
    /** Automatique coupé sur un compte sans fuseau : la liste est ouverte, rien n'est parti. */
    manualDraft: boolean
    errorMessage: string | null
}

export type TimezoneUiAction =
    | { type: "SEND_AUTO"; auto: boolean }
    | { type: "SEND_TIMEZONE"; timezone: string }
    | { type: "OPEN_DRAFT" }
    | { type: "SAVED" }
    | { type: "FAILED"; message: string }

export const INITIAL_TIMEZONE_UI: TimezoneUiState = {
    pendingAuto: null,
    pendingTimezone: null,
    manualDraft: false,
    errorMessage: null,
}

export function timezoneUiReducer(state: TimezoneUiState, action: TimezoneUiAction): TimezoneUiState {
    switch (action.type) {
        case "SEND_AUTO":
            return { ...state, pendingAuto: action.auto, pendingTimezone: null, errorMessage: null }
        case "SEND_TIMEZONE":
            return { ...state, pendingTimezone: action.timezone, errorMessage: null }
        case "OPEN_DRAFT":
            return { ...state, manualDraft: true, errorMessage: null }
        case "SAVED":
            return INITIAL_TIMEZONE_UI
        case "FAILED":
            // Le brouillon reste ouvert : le refus d'un choix ne referme pas la liste.
            return { ...state, pendingAuto: null, pendingTimezone: null, errorMessage: action.message }
        default:
            return state
    }
}

/** L'interrupteur à l'écran : le geste en cours d'abord, puis le brouillon, puis le compte. */
export function displayedAuto(ui: TimezoneUiState, auto: boolean): boolean {
    if (ui.pendingAuto != null) return ui.pendingAuto
    return ui.manualDraft ? false : auto
}

export type AccountTimezoneStateArgs = {
    timezone: string | null
    auto: boolean
    /** Demande la liste des fuseaux — appelé au geste qui coupe l'automatique. */
    onManual: () => void
}

export function useAccountTimezoneState(args: AccountTimezoneStateArgs) {
    const { timezone, auto, onManual } = args
    const navigation = useNavigation<{
        canGoBack: () => boolean
        goBack: () => void
        replace: (name: string) => void
    }>()
    const [ui, dispatchUi] = useReducer(timezoneUiReducer, INITIAL_TIMEZONE_UI)

    const { start, requesting } = useApiMutation({
        service: authService.updateTimezone,
        fallback: TIMEZONE_SAVE_FAILED,
        onSuccess: () => dispatchUi({ type: "SAVED" }),
        onError: (message) => dispatchUi({ type: "FAILED", message }),
    })

    const shownAuto = displayedAuto(ui, auto)

    const onToggleAuto = useCallback(
        (next: boolean) => {
            if (requesting) return
            if (next) {
                dispatchUi({ type: "SEND_AUTO", auto: true })
                start({}, { auto: true })
                return
            }
            onManual()
            if (!timezone) {
                dispatchUi({ type: "OPEN_DRAFT" })
                return
            }
            dispatchUi({ type: "SEND_AUTO", auto: false })
            start({}, { auto: false, timezone })
        },
        [onManual, requesting, start, timezone]
    )

    const onSelectTimezone = useCallback(
        (id: string) => {
            if (requesting) return
            // Le fuseau déjà figé : rien à écrire.
            if (id === timezone && !auto) return
            dispatchUi({ type: "SEND_TIMEZONE", timezone: id })
            start({}, { auto: false, timezone: id })
        },
        [auto, requesting, start, timezone]
    )

    const onClose = useCallback(() => {
        if (navigation.canGoBack()) navigation.goBack()
        else navigation.replace("menu")
    }, [navigation])

    return {
        auto: shownAuto,
        /** L'interrupteur attend sa réponse : il ne se rebascule pas entre-temps. */
        autoSaving: requesting && ui.pendingAuto != null,
        savingTimezoneId: requesting ? ui.pendingTimezone : null,
        errorMessage: ui.errorMessage,
        onToggleAuto,
        onSelectTimezone,
        onClose,
    }
}
